// Walk word/document.xml into a flat, document-ordered list of paragraphs with
// their fully-resolved (cascaded) properties. Order + normalized text is how
// applyOoxmlProps re-attaches these onto the mammoth-built Plate fragment.
import { attrByLocalName, childByLocalName, elementChildren, val } from './readDocxXml';
import { parseStyles, resolveParagraphProps, resolveRunProps } from './styles';
import { parseNumbering, resolveNumberingLevel, createNumberingCounter } from './numbering';
import type { StylesData } from './styles';
import type { NumberingData, NumberingCounter } from './numbering';
import {
    EDITOR_DEFAULT_LINE_HEIGHT,
    HIGHLIGHT_NAME_TO_HEX,
    JC_TO_ALIGN,
    LINE_HEIGHT_MAX,
    LINE_HEIGHT_MIN,
    LINE_RULE_AUTO,
    NUM_ID_NONE,
    OOXML_AUTO,
    PX_PER_PT,
    TWIPS_PER_LINE,
} from './constants';
import type { DocxXmlParts, OoxmlCell, OoxmlParagraph, OoxmlRun, OoxmlSpacing, ResolvedRunProps } from './types';

export function normalizeText(raw: string): string {
    return raw.replace(/\s+/g, ' ').trim();
}

// Half-points → the editor's px-string fontSize convention (pt → px = ×4/3).
function sizeToFontSizePx(sz: number | undefined): string | undefined {
    if (sz == null) return undefined;
    const px = Math.round((sz / 2) * PX_PER_PT);
    return px > 0 ? `${px}px` : undefined;
}

function hex(value: string): string {
    return `#${value.toLowerCase()}`;
}

function toOoxmlRun(text: string, props: ResolvedRunProps, baseSz: number | undefined, baseFont: string | undefined): OoxmlRun {
    const run: OoxmlRun = { text };
    // Only stamp a font size when the run's resolved size genuinely DIFFERS from
    // the paragraph's base (docDefaults + paragraph-style) size. Uniform body /
    // heading text equals its base → no mark → renders at the editor default, so
    // an import can never make some equal-sized text bigger than the rest. Only
    // deliberate per-run overrides (a manually resized word, a character style)
    // carry an explicit size.
    if (props.sz != null && props.sz !== baseSz) {
        const fontSizePx = sizeToFontSizePx(props.sz);
        if (fontSizePx) run.fontSizePx = fontSizePx;
    }
    // Same base-diff gating as fontSize: only a run whose resolved font differs
    // from the paragraph's base font carries an explicit fontFamily.
    if (props.font && props.font !== baseFont) run.fontFamily = props.font;
    if (props.color && props.color !== OOXML_AUTO) run.color = hex(props.color);
    // Background: Google Docs writes highlights as w:shd/@w:fill (mammoth drops
    // it); Word uses w:highlight (mammoth's styleMap already recovers that in
    // PR1, but resolving it here too is harmless — applyOoxmlProps only fills a
    // mark when absent). shd wins when both are present.
    if (props.shdFill && props.shdFill !== OOXML_AUTO) {
        run.backgroundColor = hex(props.shdFill);
    } else if (props.highlight && Object.prototype.hasOwnProperty.call(HIGHLIGHT_NAME_TO_HEX, props.highlight)) {
        // Prototype-safe lookup (§5): w:highlight="__proto__" on a plain object
        // literal returns Object.prototype, whose .toLowerCase() throws — and
        // the best-effort catch in ooxml/index.ts would then silently discard
        // ALL OOXML fidelity for the whole document.
        const named = HIGHLIGHT_NAME_TO_HEX[props.highlight];
        if (named) run.backgroundColor = named.toLowerCase();
    }
    if (props.underline) run.underline = true;
    return run;
}

// w:spacing/@w:line in "auto" mode is a multiplier in 240ths of a line; exact /
// atLeast modes are absolute twips, which don't map to a unitless lineHeight —
// skip those rather than emit a wrong value.
function spacingToLineHeight(spacing: OoxmlSpacing | undefined): number | undefined {
    if (!spacing || spacing.line == null) return undefined;
    if (spacing.lineRule && spacing.lineRule !== LINE_RULE_AUTO) return undefined;
    const lineHeight = spacing.line / TWIPS_PER_LINE;
    if (!Number.isFinite(lineHeight)) return undefined;
    const clamped = Math.min(LINE_HEIGHT_MAX, Math.max(LINE_HEIGHT_MIN, lineHeight));
    return Math.round(clamped * 100) / 100;
}

// All <w:r> descendants of a paragraph in document order (runs also live inside
// <w:hyperlink>, <w:ins>, <w:smartTag>, …), without descending into a nested
// paragraph (paragraphs don't nest, but structured-document-tags can wrap them).
function runElements(el: Element, out: Element[]): void {
    for (const child of elementChildren(el)) {
        if (child.localName === 'r') out.push(child);
        else if (child.localName !== 'p') runElements(child, out);
    }
}

function runText(runEl: Element): string {
    let text = '';
    for (const child of elementChildren(runEl)) {
        if (child.localName === 't') text += child.textContent ?? '';
        else if (child.localName === 'tab') text += '\t';
        else if (child.localName === 'br' || child.localName === 'cr') text += '\n';
    }
    return text;
}

function buildParagraph(pEl: Element, stylesData: StylesData, numberingData: NumberingData, counter: NumberingCounter): OoxmlParagraph {
    const pPr = childByLocalName(pEl, 'pPr');
    const paragraphStyleId = val(childByLocalName(pPr, 'pStyle'));
    const paraProps = resolveParagraphProps(paragraphStyleId, pPr, stylesData);

    // The paragraph's base run props (docDefaults + paragraph-style chain,
    // WITHOUT the run's own direct/character-style props) — a run equal to
    // these is the paragraph's default text and gets no explicit override.
    const baseRunProps = resolveRunProps(paragraphStyleId, null, null, stylesData);
    const baseSz = baseRunProps.sz;
    const baseFont = baseRunProps.font;

    const runEls: Element[] = [];
    runElements(pEl, runEls);

    const runs: OoxmlRun[] = [];
    let rawText = '';
    for (const runEl of runEls) {
        const text = runText(runEl);
        if (text === '') continue;
        const rPr = childByLocalName(runEl, 'rPr');
        const runStyleId = val(childByLocalName(rPr, 'rStyle'));
        runs.push(toOoxmlRun(text, resolveRunProps(paragraphStyleId, runStyleId, rPr, stylesData), baseSz, baseFont));
        rawText += text;
    }

    const anchorText = normalizeText(rawText);
    const paragraph: OoxmlParagraph = { anchorText, isEmpty: anchorText === '', runs };

    if (paraProps.jc) {
        const align = JC_TO_ALIGN[paraProps.jc];
        if (align) paragraph.align = align;
    }

    // numId "0" cancels an inherited numbering (a real deliberate "not a list").
    // Computed before the lineHeight gate below, which needs to know list-ness.
    const isListItem = !!(paraProps.numPr && paraProps.numPr.numId !== NUM_ID_NONE);

    // Non-list paragraphs: honor any computed auto line spacing as authored
    // (spacingToLineHeight already clamps to [MIN, MAX]), including Word's
    // ~1.08 or Google Docs' 1.15 — even though that's tighter than the editor's
    // own 1.2 default. List paragraphs keep the old anti-cramp guard (only
    // stamp spacing LOOSER than the editor default) since list rendering
    // assumes the tighter default rhythm.
    const lineHeight = spacingToLineHeight(paraProps.spacing);
    if (lineHeight != null && (!isListItem || lineHeight > EDITOR_DEFAULT_LINE_HEIGHT)) {
        paragraph.lineHeight = lineHeight;
    }
    if (paraProps.spacing?.before != null) paragraph.spacingBefore = paraProps.spacing.before;
    if (paraProps.spacing?.after != null) paragraph.spacingAfter = paraProps.spacing.after;
    if (!isListItem && paraProps.indentLeft != null) paragraph.indentLeft = paraProps.indentLeft;

    if (isListItem) {
        const level = resolveNumberingLevel(paraProps.numPr!.numId, paraProps.numPr!.ilvl, numberingData);
        if (level) {
            paragraph.list = {
                listStyleType: level.listStyleType,
                indent: paraProps.numPr!.ilvl + 1,
                numId: paraProps.numPr!.numId,
                start: level.start,
            };
            // Word's real running number for this paragraph. The counter walks
            // the whole document in order, so — unlike the level's static
            // `start` above — it's the value that diverges from a fresh
            // positional recount after an interruption (table, style change, a
            // second numId, …). Bullets have no ordinal.
            if (level.ordered) {
                paragraph.list.ordinal = counter.next(paraProps.numPr!.numId, paraProps.numPr!.ilvl);
            }
        }
    }

    return paragraph;
}

function collectParagraphs(
    el: Element | null,
    stylesData: StylesData,
    numberingData: NumberingData,
    out: OoxmlParagraph[],
    counter: NumberingCounter,
): void {
    if (!el) return;
    for (const child of elementChildren(el)) {
        if (child.localName === 'p') {
            out.push(buildParagraph(child, stylesData, numberingData, counter));
        } else {
            // Descend into tables / structured-document-tags / other containers
            // so cell paragraphs are collected in document order too.
            collectParagraphs(child, stylesData, numberingData, out, counter);
        }
    }
}

export function extractParagraphs(parts: DocxXmlParts): OoxmlParagraph[] {
    const stylesData = parseStyles(parts.styles);
    const numberingData = parseNumbering(parts.numbering);
    const body = childByLocalName(parts.document.documentElement, 'body');
    const out: OoxmlParagraph[] = [];
    // One counter instance walks the whole document — Word's ordinals are a
    // running count per (numId, ilvl), not a per-paragraph lookup.
    collectParagraphs(body, stylesData, numberingData, out, createNumberingCounter(numberingData));
    return out;
}

// All text under a cell (its paragraphs' runs, incl. any nested table), matching
// how applyOoxmlProps concatenates a Plate cell's leaves for anchoring.
function cellText(el: Element): string {
    let text = '';
    for (const child of elementChildren(el)) {
        if (child.localName === 't') text += child.textContent ?? '';
        else text += cellText(child);
    }
    return text;
}

function collectCells(el: Element | null, out: OoxmlCell[]): void {
    if (!el) return;
    for (const child of elementChildren(el)) {
        if (child.localName === 'tc') {
            const shd = childByLocalName(childByLocalName(child, 'tcPr'), 'shd');
            const fill = shd ? attrByLocalName(shd, 'fill') : null;
            const background = fill && fill !== OOXML_AUTO ? `#${fill.toLowerCase()}` : undefined;
            out.push({ anchorText: normalizeText(cellText(child)), background });
        }
        collectCells(child, out);
    }
}

// Table cell shadings (w:tc/w:tcPr/w:shd) in document order. mammoth drops these
// entirely, so they're re-attached onto the Plate td/th nodes by applyOoxmlProps.
export function extractCellShadings(parts: DocxXmlParts): OoxmlCell[] {
    const body = childByLocalName(parts.document.documentElement, 'body');
    const out: OoxmlCell[] = [];
    collectCells(body, out);
    return out;
}
